/**
 * React Query hooks for User Permissions
 *
 * Provides queries for managing user permissions with automatic cache invalidation.
 *
 * Usage:
 * ```typescript
 * import { usePermissionsQuery } from '@/lib/platform/hooks/queries/use-permissions-query';
 *
 * function MyComponent() {
 *   const { data: permissions, isLoading } = usePermissionsQuery();
 *
 *   if (permissions?.isAdmin) {
 *     // Show admin features
 *   }
 * }
 * ```
 */

'use client';

import { fetchWithBypass } from '../../../platform/utils/fetch';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from './query-keys';
import { useOrganization } from '@/lib/platform/contexts/organization-context';
import { useSession } from '@/lib/platform/auth/client';
import { permissionsEvents } from '@/lib/platform/permissions/events';
import { useEffect } from 'react';
import { logger } from '@/lib/platform/utils/logger';

// UPDATED: 2025-12-04 - Renamed to match menu structure
type RoleType =
  | 'admin'
  | 'dashboard'
  | 'analytics'
  | 'reports'
  | 'methods'
  | 'valuations_sales'
  | 'valuations_transfer'
  | 'valuations_marketing'
  | 'valuations_brand'
  | 'approver'
  | 'sponsors'
  | 'packages'
  | 'inventory'
  | 'proposals'
  | 'users'
  | 'organisation'
  | 'billing'
  | 'plugins'
  | 'api'
  | 'sso'
  | 'audit_log'
  | 'documents';

export interface Permissions {
  isAdmin: boolean;
  roles: RoleType[];
  canViewSales: boolean;
  canEditSales: boolean;
  canViewTransfer: boolean;
  canEditTransfer: boolean;
  canViewMarketing: boolean;
  canEditMarketing: boolean;
  canViewBrand: boolean;
  canEditBrand: boolean;
  canEditSettings: boolean;
  canManageUsers: boolean;
  canEditPlugins: boolean;
  canApprove: boolean;
  // Sponsorships permissions
  canViewSponsors: boolean;
  canEditSponsors: boolean;
  canViewPackages: boolean;
  canEditPackages: boolean;
  canViewLibrary: boolean;
  canEditLibrary: boolean;
  canViewProposals: boolean;
  canEditProposals: boolean;
  // Platform permissions
  canViewSSO: boolean;
  canEditSSO: boolean;
  canViewAuditLog: boolean;
  canManageAPI: boolean;
}

const DEFAULT_PERMISSIONS: Permissions = {
  isAdmin: false,
  roles: [],
  canViewSales: false,
  canEditSales: false,
  canViewTransfer: false,
  canEditTransfer: false,
  canViewMarketing: false,
  canEditMarketing: false,
  canViewBrand: false,
  canEditBrand: false,
  canEditSettings: false,
  canManageUsers: false,
  canEditPlugins: false,
  canApprove: false,
  // Sponsorships permissions
  canViewSponsors: false,
  canEditSponsors: false,
  canViewPackages: false,
  canEditPackages: false,
  canViewLibrary: false,
  canEditLibrary: false,
  canViewProposals: false,
  canEditProposals: false,
  // Platform permissions
  canViewSSO: false,
  canEditSSO: false,
  canViewAuditLog: false,
  canManageAPI: false,
};

/**
 * Fetch user permissions for an organization
 */
async function fetchPermissions(organizationId: string): Promise<Permissions> {
  const response = await fetchWithBypass(`/api/permissions?organizationId=${organizationId}`);

  if (!response.ok) {
    const error = await response.json();
    logger.error('[usePermissionsQuery] Failed to fetch permissions:', error);
    return DEFAULT_PERMISSIONS;
  }

  const data = await response.json();
  return data;
}

/**
 * Hook to fetch and cache user permissions for an organization
 *
 * Features:
 * - Automatic caching (5 minutes stale time)
 * - Background refetching
 * - Automatic retries on failure
 * - Listens to permission events for real-time updates
 */
export function usePermissionsQuery() {
  const { organization } = useOrganization();
  const { data: session } = useSession();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.permissions.user(session?.user?.id ?? '', organization?.id ?? ''),
    queryFn: () => fetchPermissions(organization?.id ?? ''),
    enabled: !!organization?.id && !!session?.user?.id, // Only fetch if both are available
    staleTime: 30 * 60 * 1000, // 30 minutes (permissions rarely change for current user)
    gcTime: 60 * 60 * 1000, // 60 minutes
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnReconnect: false, // Don't refetch when internet reconnects
    placeholderData: DEFAULT_PERMISSIONS, // Use default permissions while loading
  });

  // Listen for permissions update events
  useEffect(() => {
    if (!organization?.id || !session?.user?.id) return;

    const unsubscribe = permissionsEvents.subscribe(() => {
      // Invalidate cache and refetch when permissions are updated
      queryClient.invalidateQueries({
        queryKey: queryKeys.permissions.user(session.user.id, organization.id),
      });
    });

    return unsubscribe;
  }, [organization?.id, session?.user?.id, queryClient]);

  return query;
}
